import { Set as ImmutableSet } from "immutable";

import type { TMessageRefs, TSentMessages } from "./ChatContainer";

export const isToday = (todayString: string, givenDateString: string) => {
	const today = new Date(todayString);
	const givenDate = new Date(givenDateString);

	const givenYear = givenDate.getUTCFullYear();
	const givenMonth = givenDate.getUTCMonth();
	const givenDay = givenDate.getUTCDate();

	const todayYear = today.getUTCFullYear();
	const todayMonth = today.getUTCMonth();
	const todayDay = today.getUTCDate();

	return givenYear === todayYear && givenMonth === todayMonth && givenDay === todayDay;
};

export const monthList = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export const toSetLastSeen = (dateString: string | null, isDeleted: boolean) => {
	if (isDeleted) return "last seen a long time ago";
	if (dateString === null) return "last seen a long time ago";

	const now = new Date();
	const pastDate = new Date(dateString);
	const diffMs = now.getTime() - pastDate.getTime();

	const seconds = Math.floor(diffMs / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);
	const weeks = Math.floor(days / 7);
	const months = Math.floor(days / 30);
	// const years = Math.floor(days / 365);

	if (seconds < 60) {
		return "last seen just now";
	} else if (minutes < 60) {
		return `last seen ${minutes} minute${minutes > 1 ? "s" : ""} ago`;
	} else if (hours < 24) {
		return `last seen ${hours} hour${hours > 1 ? "s" : ""} ago`;
	} else if (days < 7) {
		return `last seen ${days} day${days > 1 ? "s" : ""} ago`;
	} else if (weeks < 4) {
		return `last seen ${weeks} week${weeks > 1 ? "s" : ""} ago`;
	} else if (months < 12) {
		return `last seen ${months} month${months > 1 ? "s" : ""} ago`;
	} else {
		return "last seen a long time ago";
	}
};

export const readPreviousMessagesUntilTarget = (
	messageTargetId: string,
	messageCompanionRefs: React.MutableRefObject<TMessageRefs>,
	setSentMessagesMap: React.Dispatch<React.SetStateAction<TSentMessages>>,
	currentChat: string,
): void => {
	let startMapping = false;
	const getValues = messageCompanionRefs.current.entrySeq();
	let newSentMessagesMap: ImmutableSet<string> = ImmutableSet();

	for (const [key, value] of getValues) {
		if (value.data.read_at === null) {
			startMapping = true;
		}

		if (startMapping) {
			newSentMessagesMap = newSentMessagesMap.add(key);
			if (messageTargetId === key) {
				break;
			}
		}
	}

	setSentMessagesMap((prev) => prev.set(currentChat, newSentMessagesMap));
	return;
};

type Timer = ReturnType<typeof setTimeout>;

export interface DebouncedFunction<T extends (...args: any[]) => void> {
	(...args: Parameters<T>): void;
	cancel: () => void;
}

export function debounce<T extends (...args: any[]) => void>(func: T, wait: number = 200): DebouncedFunction<T> {
	let timeout: Timer | undefined;

	function executedFunction(...args: Parameters<T>) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	}

	executedFunction.cancel = () => {
		clearTimeout(timeout);
	};

	return executedFunction as DebouncedFunction<T>;
}
